import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';
import { ChakraProvider } from '@chakra-ui/react';
import { chakraTheme } from './chakraTheme';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from '../../containers/prescription/usePrescription/helpers.mock';
import { materialTheme } from './materialTheme';
import { BaseThemeColors } from './interfaces/baseThemeColors';

interface ThemeProviderProps {
  children: ReactNode;
}

interface ThemeContextProps {
  themeColor: BaseThemeColors;
  setThemeColor(themeColor: BaseThemeColors): void;
}

export const ThemeContext = createContext<ThemeContextProps>({
  themeColor: { primary: DEFAULT_PRIMARY_COLOR, secondary: DEFAULT_SECONDARY_COLOR },
  setThemeColor: (themeColor: BaseThemeColors) => {},
});

/**
 * Converte uma cor hexadecimal em seus canais RGB.
 *
 * A cor primária é definida pelo parceiro e pode chegar em formatos inesperados;
 * por isso a função é tolerante e sinaliza falha com `null` em vez de lançar,
 * permitindo ao chamador preservar os tints default declarados em index.css.
 *
 * @param hex - Cor em notação hexadecimal, com ou sem `#` (3 ou 6 dígitos)
 * @returns Tupla `[r, g, b]` no intervalo 0–255, ou `null` se `hex` for inválido
 */
function hexToRgb(hex: string): [number, number, number] | null {
  const value = hex.trim().replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const int = parseInt(full, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/**
 * Clareia uma cor misturando-a com branco no espaço sRGB.
 *
 * Reproduz em JavaScript o `color-mix(in srgb, cor X%, #fff)` do CSS, que não é
 * suportado nos browsers antigos do ambiente MV. É o cálculo que deriva os tons
 * soft/muted/border a partir da cor primária do parceiro.
 *
 * @param rgb - Canais `[r, g, b]` (0–255) da cor de origem
 * @param ratio - Peso da cor de origem na mistura, de 0 (branco) a 1 (cor pura)
 * @returns Cor resultante em notação hexadecimal `#rrggbb`
 */
function mixWithWhite([r, g, b]: [number, number, number], ratio: number): string {
  const mix = (channel: number) => Math.round(channel * ratio + 255 * (1 - ratio));
  return `#${[mix(r), mix(g), mix(b)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Provê o tema da aplicação a partir da cor do parceiro, integrando Chakra,
 * styled-components e as utilities do Tailwind.
 *
 * A cor primária e seus tints derivados são publicados como CSS custom properties
 * em `:root`, que são a fonte consumida tanto pelas classes Tailwind quanto pelos
 * estilos inline — mantendo a identidade visual do parceiro consistente em runtime.
 *
 * @param props.children - Árvore de componentes que recebe o tema
 */
function ThemeProvider(props: ThemeProviderProps) {
  const [themeColor, setThemeColor] = useState<BaseThemeColors>({
    primary: DEFAULT_PRIMARY_COLOR,
    secondary: DEFAULT_SECONDARY_COLOR,
  });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeColor.primary);
    root.style.setProperty('--color-secondary', themeColor.secondary);

    // Os tints do primary são derivados aqui, e não mais via color-mix no CSS, porque
    // o color-mix não roda nos browsers antigos do ambiente MV. Alimentam tanto as
    // classes Tailwind (primary-soft/muted/border e o alpha de bg-primary, via
    // --color-primary-rgb) quanto os var(--color-primary-*) usados em estilos inline.
    const rgb = hexToRgb(themeColor.primary);
    if (rgb) {
      root.style.setProperty('--color-primary-rgb', `${rgb[0]} ${rgb[1]} ${rgb[2]}`);
      root.style.setProperty('--color-primary-soft', mixWithWhite(rgb, 0.1));
      root.style.setProperty('--color-primary-muted', mixWithWhite(rgb, 0.45));
      root.style.setProperty('--color-primary-border', mixWithWhite(rgb, 0.3));
    }
  }, [themeColor]);

  return (
    <ThemeContext.Provider
      value={{
        themeColor,
        setThemeColor,
      }}
    >
      <ChakraProvider theme={chakraTheme(themeColor)}>
        <StyledComponentsThemeProvider theme={materialTheme(themeColor)}>
          {props.children}
        </StyledComponentsThemeProvider>
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
